'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Avatar, Button, Input, Icon } from '@/components/ds';

type ClientRow = {
  id: string;
  fullName: string | null;
  phone: string;
  bookingCount: number;
  // tRPC serialises Date to ISO string without a transformer. Null for a
  // manually-added contact (client.create) with no bookings yet.
  lastBookingAt: string | Date | null;
};

/** Small info pill — count/date at a glance, not an action (no filter semantics). */
function StatChip({
  icon,
  children,
}: {
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        height: 30,
        paddingInline: '10px',
        borderRadius: 'var(--r-full)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--bg-sunken)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--fs-caption)',
        whiteSpace: 'nowrap',
        flex: '0 0 auto',
      }}
    >
      <Icon name={icon} size={13} color="var(--text-tertiary)" aria-hidden />
      {children}
    </div>
  );
}

export default function ClientsPage() {
  const { t } = useTranslation();
  const router = useRouter();

  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  // Derive everything from the query — no local accumulation needed.
  const listQuery = trpc.clients.list.useQuery(
    { search: search.trim() || undefined, limit: 20, cursor },
    { retry: 1 },
  );

  const clients: ClientRow[] = listQuery.data?.clients ?? [];
  const nextCursor = listQuery.data?.nextCursor;

  const handleSearch = (val: string) => {
    setSearch(val);
    setCursor(undefined); // Reset to page 1 on new search.
  };

  return (
    <div style={{ maxInlineSize: '800px', marginInline: 'auto', padding: '2rem 1.5rem' }}>
      {/* Title */}
      <h1
        style={{
          fontSize: 'var(--fs-title1)',
          fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
          fontFamily: 'var(--font-sans)',
          marginBlockEnd: '1.5rem',
          color: 'var(--text-primary)',
        }}
      >
        {t('clients.title')}
      </h1>

      {/* Search */}
      <div style={{ marginBlockEnd: '1.25rem', maxInlineSize: '320px' }}>
        <Input
          type="search"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('clients.search_placeholder')}
          aria-label={t('clients.search_placeholder')}
          leading={<Icon name="search" size={16} aria-hidden />}
        />
      </div>

      {/* Loading */}
      {listQuery.isPending && (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)' }}>
          {t('common.loading')}
        </p>
      )}

      {/* Error */}
      {listQuery.error && (
        <p role="alert" style={{ color: 'var(--error)', fontFamily: 'var(--font-sans)' }}>
          {t('common.error')}: {listQuery.error.message}
        </p>
      )}

      {/* Empty state */}
      {!listQuery.isPending && !listQuery.error && clients.length === 0 && (
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-sans)' }}>
          {t('clients.no_clients')}
        </p>
      )}

      {/* Client list */}
      {clients.length > 0 && (
        <div
          role="list"
          aria-label={t('clients.title')}
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--r-lg)',
            overflow: 'hidden',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          {clients.map((client) => (
            <div
              key={client.id}
              role="listitem"
              data-testid="client-row"
              onClick={() => router.push(`/dashboard/clients/${client.id}`)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-4)',
                padding: '14px 16px',
                borderBottom: '1px solid var(--border-subtle)',
                cursor: 'pointer',
                transition: 'background var(--dur-micro)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--bg-sunken)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '';
              }}
            >
              <Avatar name={client.fullName ?? client.phone} size={40} />

              {/* Identity — name, phone stacked below */}
              <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Link
                  href={`/dashboard/clients/${client.id}`}
                  style={{
                    color: 'var(--text-primary)',
                    fontFamily: 'var(--font-sans)',
                    fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'],
                    fontSize: 'var(--fs-body-l)',
                    textDecoration: 'none',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {client.fullName ?? <bdi>{client.phone}</bdi>}
                </Link>
                <span
                  style={{
                    color: 'var(--text-secondary)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--fs-caption)',
                  }}
                >
                  <bdi dir="ltr">{client.phone}</bdi>
                </span>
              </div>

              {/* Stat chips — appointment count + last visit */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                <StatChip icon="calendar-check">
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <strong style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                      {client.bookingCount}
                    </strong>{' '}
                    {t('clients.appointments_chip_label')}
                  </span>
                </StatChip>
                <StatChip icon="clock">
                  {client.lastBookingAt
                    ? new Date(client.lastBookingAt).toLocaleDateString('he-IL')
                    : t('clients.no_visits_yet')}
                </StatChip>
              </div>

              {/* Chevron */}
              <div
                role="presentation"
                style={{ display: 'flex', alignItems: 'center', color: 'var(--text-tertiary)' }}
              >
                <Icon name="chevron-left" size={16} aria-hidden />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Load more — cursor-based pagination. */}
      {nextCursor && (
        <div style={{ marginBlockStart: '1.25rem', textAlign: 'center' }}>
          <Button variant="secondary" size="sm" onClick={() => setCursor(nextCursor)}>
            {t('clients.next_page')}
          </Button>
        </div>
      )}
    </div>
  );
}
